// Phase I0.3 — Countdown primitive.
// HRS / MIN / SEC blocks (Bebas Neue 38px) by default; pass `compact`
// for a single inline mono HH:MM:SS readout. Updates once per second.

import { useEffect, useState, type CSSProperties } from "react";

export interface CountdownProps {
  /** Target ISO timestamp. */
  targetISO: string;
  /** Render the inline mono variant instead of three stat blocks. */
  compact?: boolean;
  style?: CSSProperties;
}

export function Countdown({ targetISO, compact, style }: CountdownProps) {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const target = new Date(targetISO).getTime();
  const diff = Math.max(0, target - now);
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  const pad2 = (n: number) => String(n).padStart(2, "0");

  if (compact) {
    return (
      <span
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          letterSpacing: 0.5,
          color: "var(--sa-accent-saffron)",
          ...style,
        }}
      >
        {pad2(h)}:{pad2(m)}:{pad2(s)}
      </span>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, ...style }}>
      {(
        [
          ["HRS", h],
          ["MIN", m],
          ["SEC", s],
        ] as const
      ).map(([lbl, v]) => (
        <div key={lbl} style={{ textAlign: "center" }}>
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 38,
              lineHeight: 0.9,
              color: "var(--sa-fg-primary)",
              letterSpacing: 1,
            }}
          >
            {pad2(v)}
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 9,
              letterSpacing: "0.24em",
              color: "var(--sa-fg-muted)",
              marginTop: 4,
            }}
          >
            {lbl}
          </div>
        </div>
      ))}
    </div>
  );
}
